import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from '@puppycad/proto'
import { downloadCadFile } from '@/services/api/cad-session'
import {
  createCachedPartResult,
  disposePartAsset,
  parseGlb,
  parsePartAssetGltf,
  type BRepRegistrar,
  type CachedPartResult,
  type PartAssetCache,
} from '@puppycad/cad-core'
import {
  throwIfImportedModelRequestStale,
  type ImportedModelLoadGuard,
} from './imported-model-load-guard'
import { ImportedModelLoadTransaction } from './imported-model-load-transaction'
import { hydrateImportedPartAssetBrepRuntime } from './imported-model-brep-loader'
import type { ImportedBrepRuntimeCache } from './imported-model-brep-runtime-cache'
import type { ImportedModelFileBytesReader } from './imported-model-loader'

export type LoadPartModelOptions = {
  projectFiles: FileInfo[]
  version: string
  partAssetCache: PartAssetCache
  sourceGltf?: GLTF
  guard?: ImportedModelLoadGuard
  importedBrepRuntimeCache?: ImportedBrepRuntimeCache
  brepRegistrar?: BRepRegistrar
  /** 可选文件读取器；轻量 ZIP 节点包通过它从内存 entry 读取 GLB 和 sidecar。 */
  readFileBytes?: ImportedModelFileBytesReader
  /**
   * 语义资产 staging 的目标事务。loader 必须通过它完成 `stagePartAsset`，
   * 禁止再直写 `partAssetCache.set()`——commit / abort 的责任在事务本身。
   */
  transaction: ImportedModelLoadTransaction
}

export async function loadPartModel(
  sessionId: string,
  file: FileInfo,
  options: LoadPartModelOptions,
): Promise<CachedPartResult | null> {
  const cachedAsset = options.partAssetCache.get(
    file.uuid,
    options.version,
    options.transaction.publishScopeKey,
  )
  if (cachedAsset) {
    throwIfImportedModelRequestStale(options.guard)
    return createCachedPartResult(cachedAsset, file)
  }

  let parsedAsset: ReturnType<typeof parsePartAssetGltf>['asset'] | null = null

  // 错误语义（2026-04 fail-fast 修复）：
  // loader 不再吞掉非 abort 异常。原先的 `logger.error + return null` 会让 caller
  // （useEditorGlbLoader / loadInsertedCadModel）以为“解析成功但无结果”，继续挂载
  // 部分场景；网络错误 / 解析错误下必须向上 reject，让 transaction 在 caller 侧正确 abort。
  // parsedAsset 只在“已解析成功但随后 stale guard 触发 abort”这类极窄窗口出现，此时必须
  // dispose 防止泄漏；之后原样 rethrow。
  try {
    const gltf = options.sourceGltf
      ? options.sourceGltf
      : await (async () => {
          const loader = new GLTFLoader()
          throwIfImportedModelRequestStale(options.guard)
          const bytes = options.readFileBytes
            ? await options.readFileBytes(file)
            : await downloadCadFile(sessionId, file.fileId, file.uuid)
          throwIfImportedModelRequestStale(options.guard)
          return await parseGlb(loader, bytes)
        })()
    throwIfImportedModelRequestStale(options.guard)

    const partResult = parsePartAssetGltf(gltf, file, options.version)
    parsedAsset = partResult.asset
    await hydrateImportedPartAssetBrepRuntime(sessionId, file, partResult.asset, {
      projectFiles: options.projectFiles,
      importedBrepRuntimeCache: options.importedBrepRuntimeCache,
      brepRegistrar: options.brepRegistrar,
      readFileBytes: options.readFileBytes,
      guard: options.guard,
    })
    throwIfImportedModelRequestStale(options.guard)

    // 语义 publish 统一走 transaction staging，禁止直写 partAssetCache.set()。
    options.transaction.stagePartAsset(partResult.asset)
    parsedAsset = null
    return createCachedPartResult(partResult.asset, file)
  } catch (error) {
    if (parsedAsset) {
      disposePartAsset(parsedAsset)
    }
    throw error
  }
}
